// IndexedDB implementation of the engine's KVStore interface. WAL records,
// SSTable buffers, and the manifest all live here, so a page refresh is a
// crash: volatile state dies, and reopening the engine replays the WAL.

import type { KVStore } from "../engine/store";

const DB_NAME = "lsm-viz";
const TABLES = ["wal", "sst", "meta"];

export class IdbStore implements KVStore {
  private constructor(private readonly db: IDBDatabase) {}

  static open(): Promise<IdbStore> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        for (const t of TABLES) {
          if (!req.result.objectStoreNames.contains(t)) {
            req.result.createObjectStore(t);
          }
        }
      };
      req.onsuccess = () => resolve(new IdbStore(req.result));
      req.onerror = () => reject(req.error);
    });
  }

  private tx<T>(
    table: string,
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(table, mode);
      const req = fn(tx.objectStore(table));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async get(table: string, key: string): Promise<unknown | undefined> {
    return this.tx(table, "readonly", (s) => s.get(key));
  }

  async put(table: string, key: string, value: unknown): Promise<void> {
    await this.tx(table, "readwrite", (s) => s.put(value, key));
  }

  async delete(table: string, key: string): Promise<void> {
    await this.tx(table, "readwrite", (s) => s.delete(key));
  }

  async entries(table: string): Promise<[string, unknown][]> {
    const [keys, vals] = await Promise.all([
      this.tx(table, "readonly", (s) => s.getAllKeys()),
      this.tx(table, "readonly", (s) => s.getAll()),
    ]);
    // IndexedDB returns keys in ascending order, matching MemoryStore.
    return (keys as string[]).map((k, i) => [k, (vals as unknown[])[i]]);
  }

  async clear(table: string): Promise<void> {
    await this.tx(table, "readwrite", (s) => s.clear());
  }
}
